import { useState, useEffect, useCallback } from 'react';

// Liste des sections du dashboard avec leurs configurations par défaut
export const DASHBOARD_SECTIONS = {
  stories: { id: 'stories', label: 'Stories / Moments', icon: '📸', defaultVisible: true },
  profile: { id: 'profile', label: 'Mon Profil', icon: '👤', defaultVisible: true, canHide: false },
  progression: { id: 'progression', label: 'Ta Progression', icon: '📊', defaultVisible: true },
  xpRewards: { id: 'xpRewards', label: 'Mon XP & Récompenses', icon: '⚡', defaultVisible: true },
  challenges: { id: 'challenges', label: 'Mes Défis', icon: '🎯', defaultVisible: true },
  odyssey: { id: 'odyssey', label: 'Mon Odyssée', icon: '🚢', defaultVisible: true },
  season: { id: 'season', label: 'Saison en cours', icon: '🌊', defaultVisible: true },
  quickNav: { id: 'quickNav', label: 'Navigation rapide', icon: '🧭', defaultVisible: true },
  events: { id: 'events', label: 'Événements à venir', icon: '📅', defaultVisible: true },
  calendar: { id: 'calendar', label: 'Calendrier', icon: '🗓️', defaultVisible: true },
  messages: { id: 'messages', label: 'Messages importants', icon: '🔔', defaultVisible: true },
  ideas: { id: 'ideas', label: 'Idées populaires', icon: '💡', defaultVisible: true },
  victories: { id: 'victories', label: 'Dernières victoires', icon: '🏆', defaultVisible: true },
  leaderboard: { id: 'leaderboard', label: 'Top contributeurs', icon: '👑', defaultVisible: true },
  vigie: { id: 'vigie', label: 'La Vigie Immobilière', icon: '🔭', defaultVisible: true },
  questions: { id: 'questions', label: 'Q&A de la formation', icon: '❓', defaultVisible: true },
};

// Ordre par défaut des sections
export const DEFAULT_SECTION_ORDER = [
  'stories', 'profile', 'quickNav', 'xpRewards', 'calendar', 'events', 'challenges', 
  'progression', 'season', 'leaderboard', 'victories',
  'odyssey', 'ideas', 'messages', 'vigie', 'questions'
];

const STORAGE_KEY = 'odys_dashboard_prefs';

// Hook pour gérer les préférences du dashboard
export const useDashboardPreferences = () => {
  const [preferences, setPreferences] = useState(() => {
    if (typeof window === 'undefined') {
      return getDefaultPreferences();
    }
    
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const defaults = getDefaultPreferences();
        // Deep merge visibility: new sections get default visibility
        const mergedVisibility = { ...defaults.visibility, ...(parsed.visibility || {}) };
        // Deep merge collapsed
        const mergedCollapsed = { ...defaults.collapsed, ...(parsed.collapsed || {}) };
        // Merge order: add new sections that are not in saved order
        const savedOrder = parsed.order || [];
        const allSectionIds = Object.keys(DASHBOARD_SECTIONS);
        const missingSections = allSectionIds.filter(id => !savedOrder.includes(id));
        const mergedOrder = [...savedOrder, ...missingSections];
        
        return { 
          visibility: mergedVisibility, 
          collapsed: mergedCollapsed, 
          order: mergedOrder 
        };
      }
    } catch (e) {
      console.error('Error loading dashboard preferences:', e);
    }
    
    return getDefaultPreferences();
  });

  // Sauvegarde automatique
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (e) {
      console.error('Error saving dashboard preferences:', e);
    }
  }, [preferences]);

  // Toggle la visibilité d'une section
  const toggleSection = useCallback((sectionId) => {
    setPreferences(prev => ({
      ...prev,
      visibility: {
        ...prev.visibility,
        [sectionId]: !prev.visibility[sectionId]
      }
    }));
  }, []);

  // Toggle l'état plié/déplié d'une section
  const toggleCollapsed = useCallback((sectionId) => {
    setPreferences(prev => ({
      ...prev,
      collapsed: {
        ...prev.collapsed,
        [sectionId]: !prev.collapsed[sectionId]
      }
    }));
  }, []);

  // Mettre à jour l'ordre des sections
  const updateSectionOrder = useCallback((newOrder) => {
    setPreferences(prev => ({
      ...prev,
      order: newOrder
    }));
  }, []);

  // Réinitialiser aux valeurs par défaut
  const resetToDefaults = useCallback(() => {
    setPreferences(getDefaultPreferences());
  }, []);

  // Vérifier si une section est visible
  const isSectionVisible = useCallback((sectionId) => {
    return preferences.visibility[sectionId] !== false;
  }, [preferences.visibility]);

  // Vérifier si une section est pliée
  const isSectionCollapsed = useCallback((sectionId) => {
    return preferences.collapsed[sectionId] === true;
  }, [preferences.collapsed]);

  // Obtenir l'ordre des sections
  const getSectionOrder = useCallback(() => {
    return preferences.order || DEFAULT_SECTION_ORDER;
  }, [preferences.order]);

  return {
    preferences,
    toggleSection,
    toggleCollapsed,
    updateSectionOrder,
    resetToDefaults,
    isSectionVisible,
    isSectionCollapsed,
    getSectionOrder,
    setPreferences
  };
};

// Obtenir les préférences par défaut
function getDefaultPreferences() {
  const visibility = {};
  const collapsed = {};
  
  Object.values(DASHBOARD_SECTIONS).forEach(section => {
    visibility[section.id] = section.defaultVisible;
    collapsed[section.id] = false; // Toutes les sections dépliées par défaut
  });
  
  return { 
    visibility, 
    collapsed, 
    order: DEFAULT_SECTION_ORDER 
  };
}

export default useDashboardPreferences;
